import type { Vector2, Vector4 } from 'three';

import type PlanogramPoint from 'shared/utils/PlanogramPoint';
import { LodLevelMask } from './helpers';
import { Operation } from './operations';

export type ImageId = string;

export type LodId = string;

export enum LodSource {
  DEFAULT = 'default',
  FIGMA = 'figma',
  BACKGRDOUND = 'background',
}

export interface TileLodData {
  url: string;
  uv?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

export interface LodLevelData {
  lod: number;
  textures: Array<null | TileLodData>;
  url_start: string;
}

// TODO: move the lods objects from root of the item into data on viewer, rename curator_lods for consistency
export interface BaseLodData {
  fit_size: [number, number];
  full_size: [number, number];
  naturalResolution: [number, number];
  lods_version: number;
}

export interface CuratorLodData extends BaseLodData {
  curator_lods: LodLevelData[];
}

export interface ImageLodData extends CuratorLodData {
  id: number;
}

export interface ExtraImageData {
  lodId: LodId;
  naturalResolution: Vector2;
  position: PlanogramPoint;
  size: Vector2;
  shading: Vector4;
  cornerRadius: Vector4;
}

export interface ImageData {
  id: ImageId;
  lodData: ImageLodData;
  mapPosition: Vector2;
  extraData: ExtraImageData;
  contentMetrics: {
    ratio: Vector2;
    offset: Vector2;
  };
}

export enum OperationDirection {
  Upgrade = 1,
  Downgrade = -1,
}

export enum WorkerMessageType {
  Initiate,
  UpdateImage,
  RemoveImage,
  Frame,
  OperationTarget,
  OperationResult,
  CleanSlots,
}

export interface InitiateMessage {
  type: WorkerMessageType.Initiate;
  loadingLimit: number;
  blacklistDuration: number;
  unloadedLevelBias: number;
  targetPixelRatio: number;
  tileMapResolution: number;
}

export interface UpdateImageMessage {
  type: WorkerMessageType.UpdateImage;
  data: ImageData;
}

export interface RemoveImageMessage {
  type: WorkerMessageType.RemoveImage;
  id: string;
}

export interface FrameMessage {
  type: WorkerMessageType.Frame;
  focusPointX: number;
  focusPointY: number;
  pixelRatio: number;
  slots: number;
}

export interface OperationTarget {
  type: WorkerMessageType.OperationTarget;
  id: number;
  operation: Operation;
}

/* TODO: consider a different operation structure
export interface OperationBatch {
  type: WorkerMessageType.OperationBatch;
  upgrades: OperationTarget[]; // TODO: remove unncesesary fields from OperationTarget
  downgrades: OperationTarget[];
}
*/

export interface CleanSlots {
  type: WorkerMessageType.CleanSlots;
  imageId: string;
  chunkMasks: Record<number, LodLevelMask>;
}

export enum OperationOutcome {
  Success,
  Failure,
  Cancelation,
}

export interface OperationResult {
  type: WorkerMessageType.OperationResult;
  id: number;
  outcome: OperationOutcome;
}

export type WorkerMessage =
  | InitiateMessage
  | UpdateImageMessage
  | RemoveImageMessage
  | FrameMessage
  | OperationTarget
  | OperationResult
  | CleanSlots;
